import React, { useState, useContext, useReducer, createContext } from 'react';
import { Icon, Form, Input, Select, Checkbox, Alert } from 'antd';
import { isValidEmail, isValidSingleName, isValidPhone } from 'utils';
import ForwardButton from 'components/ForwardButton';
import { Link } from 'react-router-dom';
import IntlCodes from 'utils/intlCodes.json';
import reducer, { TutorCruncherSignUpTutorState } from './reducer';
import {
  requestSignUpTutor,
  TutorCruncherSignUpTutorActionType
} from './actions';
import {
  TutorCruncherSignupInputUser,
  TutorCruncherSignupResponse
} from 'types/Api';
import styles from './TutorCruncherTutorSignupForm.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from 'utils/i18n';
import cn from 'classnames';
const { Option } = Select;

export default function CreateAccount() {

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [intlCode, setIntlCode] = useState('+44');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const [{ loading, tutorId, error }, dispatch] = useReducer(
    reducer,
    useContext(
      createContext({
        loading: false,
        tutorId: undefined,
        error: false
      })
    )
  ) as [
    TutorCruncherSignUpTutorState,
    React.Dispatch<{
      type: TutorCruncherSignUpTutorActionType;
      payload: { response: TutorCruncherSignupResponse } | null;
    }>
  ];

  const internationalCodeSelector = (
    <Select
      value={intlCode}
      onChange={(option: string) => updateIntlCode(option)}
    >
      {IntlCodes.countries.map((country, idx) => (
        <Option key={idx} value={country.code}>
          {country.name} ({country.code})
        </Option>
      ))}
    </Select>
  );

  const updateFirstName = (newFirstName: string) => {
    setFirstName(newFirstName);
  };

  const updateLastName = (newLastName: string) => {
    setLastName(newLastName);
  };

  const updateEmail = (newEmail: string) => {
    setEmail(newEmail);
  };

  const updateIntlCode = (newIntlCode: string) => {
    setIntlCode(newIntlCode);
  };

  const updatePhone = (newPhone: string) => {
    setPhone(newPhone);
  };

  const formIsValid = () =>
    agreeToTerms &&
    isValidPhone(phone) &&
    isValidEmail(email) &&
    isValidSingleName(firstName) &&
    isValidSingleName(lastName);
  // && recaptchaPassed;

  const submitForm = () => {
    const user: TutorCruncherSignupInputUser = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone: intlCode + phone
    };

    requestSignUpTutor(user, dispatch);
  };

  const { formContainer } = styles;
  const { t } = useTranslation()

  return (
    <>
      {tutorId ? (
        <div className={formContainer}>
          <Alert
            message={t("Success")}
            description={t("You have successfully registered as a tutor We have sent you a confirmation email with instructions for setting your password and logging in to your account")}
            type="success"
            showIcon
            className={cn(i18n.isAr&&'isarAlert')}
          />
        </div>
      ) : (
        <>
          <div className={formContainer}>
            {error && (
              <div style={{ padding: '10px' }}>
                <Alert
                  message={t("Error")}
                  description={t("Something went wrong Please try again later or get in touch with us")}
                  type="error"
                  showIcon
                  className={cn(i18n.isAr&&'isarAlert')}
                />
              </div>
            )}
            <Form layout="vertical">
              <Form.Item
                label={t("First Name")}
                hasFeedback={isValidSingleName(firstName)}
                validateStatus="success"
                help={
                  firstName.length > 2 && !isValidSingleName(firstName)
                    ? `${t('Please do not include numbers or spaces')}`
                    : ''
                }
              >
                <Input
                  value={firstName}
                  prefix={
                    <Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />
                  }
                  size="large"
                  onChange={e => updateFirstName(e.target.value)}
                  disabled={loading}
                  autoComplete="fname"
                />
              </Form.Item>
              <Form.Item
                label={t("Last Name")}
                hasFeedback={isValidSingleName(lastName)}
                validateStatus="success"
                help={
                  lastName.length > 2 && !isValidSingleName(lastName)
                    ? `${t('Please do not include numbers or spaces')}`
                    : ''
                }
              >
                <Input
                  value={lastName}
                  prefix={
                    <Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />
                  }
                  size="large"
                  onChange={e => updateLastName(e.target.value)}
                  disabled={loading}
                  autoComplete="lname"
                />
              </Form.Item>
              <Form.Item
                label={t("Email")}
                hasFeedback={isValidEmail(email)}
                validateStatus={
                  email.length > 5 && !isValidEmail(email) ? 'error' : 'success'
                }
                help={
                  email.length > 5 && !isValidEmail(email)
                    ? `${t('Please enter a valid email address')}`
                    : ''
                }
              >
                <Input
                  value={email}
                  prefix={
                    <Icon type="mail" style={{ color: 'rgba(0,0,0,.25)' }} />
                  }
                  size="large"
                  onChange={e => updateEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                />
              </Form.Item>
              <Form.Item
                label={t("Telephone")}
                hasFeedback={
                  isValidPhone(phone) ||
                  (!isValidPhone(phone) && phone.length > 5)
                }
                validateStatus={
                  !isValidPhone(phone) && phone.length > 5 ? 'error' : 'success'
                }
                help={
                  phone.length > 5 && !isValidPhone(phone)
                    ? `${t('Please enter a valid phone number without any spaces')}`
                    : ''
                }
              >
                <Input
                  value={phone}
                  addonBefore={internationalCodeSelector}
                  size="large"
                  onChange={e => updatePhone(e.target.value)}
                  disabled={loading}
                  autoComplete="phone"
                />
              </Form.Item>
              <Form.Item>
                <Checkbox
                  disabled={loading}
                  checked={agreeToTerms}
                  onChange={() => setAgreeToTerms(!agreeToTerms)}
                >
                  {t('I have read and I consent to the')}{' '}
                  <Link target="_blank" to="//www.tutorhouse.co.uk/terms">
                    {t('Terms of Service')}
                  </Link>{' '}
                  {t('and the')}{' '}
                  <Link
                    target="_blank"
                    to="//www.tutorhouse.co.uk/privacy-policy"
                  >
                    {t('Privacy Policy')}
                  </Link>
                </Checkbox>
              </Form.Item>
            </Form>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center'
            }}
          >
            <ForwardButton
              disabled={!formIsValid()}
              loading={loading}
              title={t("Sign Up")}
              onClick={submitForm}
            />
          </div>
        </>
      )}
    </>
  );
}
